import api from './api';
import type {
  Project,
  CreateProjectRequest,
  UpdateProjectRequest,
  ProjectFilters,
  PaginatedResponse,
} from '../types';

export const projectService = {
  async getAll(params?: ProjectFilters): Promise<PaginatedResponse<Project>> {
    const response = await api.get('/projects', { params });
    const raw = response.data;
    // API returns { data: [...], pagination: { page, limit, total, totalPages } }
    // Normalize to PaginatedResponse shape that components expect
    if (raw && raw.pagination) {
      return {
        data: raw.data ?? [],
        page: raw.pagination.page ?? 1,
        pageSize: raw.pagination.limit ?? 20,
        total: raw.pagination.total ?? 0,
        totalPages: raw.pagination.totalPages ?? 1,
      };
    }
    if (Array.isArray(raw)) {
      return {
        data: raw,
        page: 1,
        pageSize: raw.length,
        total: raw.length,
        totalPages: 1,
      };
    }
    return {
      data: raw?.data ?? [],
      page: raw?.page ?? 1,
      pageSize: raw?.pageSize ?? raw?.limit ?? 20,
      total: raw?.total ?? 0,
      totalPages: raw?.totalPages ?? 1,
    };
  },

  async getById(id: string): Promise<Project> {
    const response = await api.get<Project>(`/projects/${id}`);
    return response.data;
  },

  async getByClient(clientId: string): Promise<Project[]> {
    const response = await api.get(`/clients/${clientId}/projects`);
    const raw = response.data;
    // API returns array directly for client sub-routes
    if (Array.isArray(raw)) return raw;
    return raw?.data ?? [];
  },

  async create(data: CreateProjectRequest): Promise<Project> {
    const response = await api.post<Project>('/projects', data);
    return response.data;
  },

  async update(id: string, data: UpdateProjectRequest): Promise<Project> {
    const response = await api.put<Project>(`/projects/${id}`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/projects/${id}`);
  },

  async getCSATTimeline(id: string): Promise<{ date: string; score: number }[]> {
    const response = await api.get(`/projects/${id}/csat-timeline`);
    const raw = response.data;
    if (Array.isArray(raw)) return raw;
    return raw?.data ?? [];
  },
};
